import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, Form, Dropdown, Tabs, Typography } from 'antd';
import type { MenuProps, FormInstance } from 'antd';
import {
  CloseOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import CallPreview from './CallPreview';
import EmailPreview from './EmailPreview';
import type { DynVarOption } from '../hooks/useDynamicVariables';
import { CustomButton, CustomTitle } from '@/components';
import { TemplateIcon, StarTickIcon } from '@/assets/icons/icons';
import { BehaviorSection } from './BehaviorSection';
import type { AgentDrawerFormValues, AgentTemplate, Direction } from '../types';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import type { CreateCustomVariableInput } from '@/components/AddCustomVariablePanel';
import type { CustomVariableTypeName } from '@/common/collectVariableType';
import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';

const { Paragraph } = Typography;

// The AI improve-prompt service is only ever asked to suggest one of these
// three types for a new variable, named the way it names them (Text/DateTime/
// Boolean) rather than the CustomVariableTypeName the API actually stores.
// Anything else (a malformed response, a model drift) falls back to 'TEXT'.
const AI_SUGGESTED_VAR_TYPE_MAP: Record<string, CustomVariableTypeName> = {
  text: 'TEXT',
  datetime: 'DATE_TIME',
  boolean: 'BOOL',
};
const normalizeAiSuggestedVarType = (type?: string): CustomVariableTypeName =>
  AI_SUGGESTED_VAR_TYPE_MAP[(type || '').toLowerCase()] || 'TEXT';

type Option = {
  value: string | number;
  label: string;
  type?: CustomVariableTypeName;
  isCustomVar?: boolean;
  description?: string;
  sample_value?: string;
  is_in_use?: boolean;
  isPending?: boolean;
};

export type GenerateResp = {
  improved_prompt?: string;
  reason?: string;
  collect_variables?: string[];
  new_custom_variables?: {
    name: string;
    description?: string;
    type?: string;
    sample_value?: string;
  }[];
  is_valid?: boolean;
  issues?: string[];
  generated_email?: {
    subject?: string;
    email_body?: string;
    email_body_rendered?: string;
    notes?: string;
  };
  call_transcript?: {
    transcript?: string;
    transcript_rendered?: string;
    notes?: string;
  };
};

type Props = {
  direction: Direction;
  templates: AgentTemplate[];
  templatesLoading?: boolean;
  onSelectTemplate?: (tpl: AgentTemplate) => void;
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  dynVarOptions: DynVarOption[];
  onOpenDyn: () => void;
  loadingDropdowns: boolean;
  form: FormInstance<AgentDrawerFormValues>;
  collectOptions: Option[];
  genReqIdRef: React.MutableRefObject<number>;
  onGenerateInstructions?: (
    reqId: number,
    input: { behavior_prompt: string; collect_ids: (string | number)[] }
  ) => Promise<GenerateResp | void>;
  isEditing?: boolean;
  userName?: string;
  emailSignature?: string;
  companyName?: string;
  onDeleteCustomVar?: (id: string) => Promise<void>;
  onCreateCustomVar?: (
    input: CreateCustomVariableInput
  ) => Promise<string | number | void>;
  onUpdateCustomVar?: (
    id: string | number,
    input: CreateCustomVariableInput
  ) => void | Promise<void>;
};

type TemplateCollectItem = { collect_id: string | number };

type AgentTemplateForModal = AgentTemplate & {
  behavior_prompt?: string | null;
  collect?: TemplateCollectItem[] | null;
};

const normalizeArr = (arr: string[]) => [...arr].map(String).sort().join('|');

const getTemplatePrefill = (
  tpl: AgentTemplate
): { behavior_instructions: string; collect: string[] } => {
  // The templates API also returns behavior_prompt/collect, which
  // AgentTemplate doesn't declare.
  const t = tpl as AgentTemplateForModal;
  const behavior_instructions = String(t.behavior_prompt ?? '');

  const collect = Array.isArray(t.collect)
    ? t.collect
        .map((c) => c.collect_id)
        .filter(
          (x): x is string => typeof x === 'string' || typeof x === 'number'
        )
        .map((x) => String(x))
    : [];

  return { behavior_instructions, collect };
};

type CallTranscriptMessage = { sender?: string; text: string };

const parseCallTranscript = (raw?: string): CallTranscriptMessage[] => {
  if (!raw) return [];
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const lower = line.toLowerCase();
      const isAgent = lower.startsWith('agent:');
      const text = line.replace(/^agent:\s*/i, '').replace(/^user:\s*/i, '');
      return { sender: isAgent ? 'agent' : 'user', text: text.trim() };
    });
};

const BestPracticesPanel: React.FC<{ onCollapse: () => void }> = ({
  onCollapse,
}) => (
  <div className='agentInstrModal__bestPractices'>
    <div className='agentInstrModal__bestHeader'>
      <div className='agentInstrModal__bestTitle'>Best practices</div>
      <button
        type='button'
        className='agentInstrModal__collapseBtn'
        aria-label='Collapse best practices'
        onClick={onCollapse}
      >
        <VerticalLeftOutlined />
      </button>
    </div>

    <div className='agentInstrModal__bpSection'>
      <div className='agentInstrModal__bpHead'>
        1. State objectives &amp; goals
      </div>
      <Paragraph className='agentInstrModal__bpHint'>For example:</Paragraph>
      <ul className='agentInstrModal__bpList'>
        <li>
          “The agent is contacting the recipient to confirm whether FedEx has
          picked up the shipment and identify any blockers to pickup today”
        </li>
      </ul>
    </div>

    <div className='agentInstrModal__bpDivider' />

    <div className='agentInstrModal__bpSection'>
      <div className='agentInstrModal__bpHead'>
        2. Make sure to list the key questions
      </div>
      <Paragraph className='agentInstrModal__bpHint'>For example:</Paragraph>
      <ul className='agentInstrModal__bpList'>
        <li>Has FedEx already picked up this shipment?</li>
        <li>If not, is the shipment ready for pickup right now?</li>
        <li>
          If shipment not ready now, then what is the scheduled pickup time
          window (earliest and latest) and is it confirmed with FedEx?
        </li>
        <li>Finally, are there any issue preventing pickup?</li>
      </ul>
    </div>
  </div>
);

export const AgentInstructionsModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  dynVarOptions,
  onOpenDyn,
  loadingDropdowns,
  form,
  genReqIdRef,
  collectOptions,
  onGenerateInstructions,
  templates,
  templatesLoading,
  onSelectTemplate,
  emailSignature,
  companyName,
  onDeleteCustomVar,
  onCreateCustomVar,
  onUpdateCustomVar,
}) => {
  const [draftForm] = Form.useForm<AgentDrawerFormValues>();

  const watchedInstructions = Form.useWatch('behavior_instructions', draftForm);
  const watchedCollect = Form.useWatch('collect', draftForm) as
    string[] | undefined;

  const [showPreview, setShowPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState<'call' | 'email'>('call');
  const [genResp, setGenResp] = useState<GenerateResp | null>(null);
  const [editedAfterSnapshot, setEditedAfterSnapshot] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [missingCollectOpen, setMissingCollectOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [bestPracticesCollapsed, setBestPracticesCollapsed] = useState(false);

  const snapshotRef = useRef<{
    instructions: string;
    collect: string[];
  } | null>(null);

  const preGenRef = useRef<{
    instructions: string;
    collect: string[];
  } | null>(null);

  const postGenRef = useRef<{
    instructions: string;
    collect: string[];
  } | null>(null);

  const preGenerateUiRef = useRef<{
    showPreview: boolean;
    previewTab: 'call' | 'email';
    genResp: GenerateResp | null;
  } | null>(null);

  const isOpenRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const parentInstr = String(
      form.getFieldValue('behavior_instructions') || ''
    );
    const parentCollectRaw =
      (form.getFieldValue('collect') as (string | number)[] | undefined) || [];
    const parentCollect = Array.isArray(parentCollectRaw)
      ? parentCollectRaw.map(String)
      : [];
    const parentAdvanced = Boolean(
      form.getFieldValue('advanced_instruction_mode')
    );

    draftForm.setFieldsValue({
      behavior_instructions: parentInstr,
      collect: parentCollect,
    });

    snapshotRef.current = {
      instructions: parentInstr,
      collect: [...parentCollect],
    };

    setEditedAfterSnapshot(false);
    setIsGenerating(false);
    setShowPreview(false);
    setPreviewTab('call');
    setGenResp(null);
    setAdvancedMode(parentAdvanced);
    setMissingCollectOpen(false);
    setCollectOpen(false);
    setBestPracticesCollapsed(false);
    preGenRef.current = null;
    postGenRef.current = null;
    preGenerateUiRef.current = null;
  }, [open, form, draftForm]);

  const currentInstructions = String(
    watchedInstructions ??
      draftForm.getFieldValue('behavior_instructions') ??
      ''
  );

  const canAction = useMemo(() => {
    const hasInstr = !!currentInstructions.trim();
    return hasInstr;
  }, [currentInstructions]);

  const canUndoGenerate = !!postGenRef.current && !isGenerating;

  const canClearAll = useMemo(() => {
    const hasInstr = !!String(watchedInstructions || '').trim();
    const hasCollect =
      Array.isArray(watchedCollect) && watchedCollect.length > 0;
    return (hasInstr || hasCollect) && !isGenerating;
  }, [watchedInstructions, watchedCollect, isGenerating]);

  const handleClearAll = useCallback(() => {
    const currentInstr = String(
      draftForm.getFieldValue('behavior_instructions') || ''
    );
    const currentCollectRaw =
      (draftForm.getFieldValue('collect') as (string | number)[] | undefined) ||
      [];
    const currentCollect = currentCollectRaw.map(String);

    preGenRef.current = {
      instructions: currentInstr,
      collect: currentCollect,
    };

    preGenerateUiRef.current = {
      showPreview,
      previewTab,
      genResp,
    };

    draftForm.setFieldsValue({
      behavior_instructions: '',
      collect: [],
    });

    postGenRef.current = {
      instructions: '',
      collect: [],
    };

    snapshotRef.current = {
      instructions: '',
      collect: [],
    };

    setEditedAfterSnapshot(false);
    setShowPreview(false);
    setGenResp(null);
  }, [draftForm, showPreview, previewTab, genResp]);

  const restorePreGenerateUi = useCallback(() => {
    const ui = preGenerateUiRef.current;

    if (!ui) {
      setShowPreview(false);
      setPreviewTab('call');
      setGenResp(null);
      return;
    }

    setShowPreview(ui.showPreview);
    setPreviewTab(ui.previewTab);
    setGenResp(ui.genResp);
  }, []);

  const handleUndoGenerate = useCallback(() => {
    const snap = preGenRef.current;
    if (!snap) return;

    genReqIdRef.current += 1;

    setIsGenerating(false);

    draftForm.setFieldsValue({
      behavior_instructions: snap.instructions,
      collect: snap.collect,
    });

    postGenRef.current = null;
    snapshotRef.current = {
      instructions: snap.instructions,
      collect: [...snap.collect],
    };

    restorePreGenerateUi();
    setEditedAfterSnapshot(false);
  }, [draftForm, genReqIdRef, restorePreGenerateUi]);

  const handleCancelGenerate = useCallback(() => {
    genReqIdRef.current += 1;

    setIsGenerating(false);

    const snap = preGenRef.current;
    if (snap) {
      draftForm.setFieldsValue({
        behavior_instructions: snap.instructions,
        collect: snap.collect,
      });
    }

    restorePreGenerateUi();
  }, [draftForm, genReqIdRef, restorePreGenerateUi]);

  const handleCloseModal = useCallback(() => {
    genReqIdRef.current += 1;

    setIsGenerating(false);
    setShowPreview(false);
    setGenResp(null);
    setPreviewTab('call');

    preGenRef.current = null;
    postGenRef.current = null;
    snapshotRef.current = null;
    preGenerateUiRef.current = null;

    setEditedAfterSnapshot(false);
    draftForm.resetFields();
    onClose();
  }, [genReqIdRef, onClose, draftForm]);

  useEffect(() => {
    if (!snapshotRef.current) return;

    const currentInstr = String(watchedInstructions || '');
    const currentCollect = Array.isArray(watchedCollect)
      ? watchedCollect.map(String)
      : [];

    const snap = snapshotRef.current;
    const instrChanged = currentInstr !== snap.instructions;
    const collectChanged =
      normalizeArr(currentCollect) !== normalizeArr(snap.collect);

    setEditedAfterSnapshot(instrChanged || collectChanged);
  }, [watchedInstructions, watchedCollect]);

  const templateItems: MenuProps['items'] =
    templates && templates.length
      ? templates.map((t) => ({
          key: String(t.id),
          label: t.purpose_name || t.name || 'Untitled template',
        }))
      : [{ key: 'no-data', label: 'No data', disabled: true }];

  const handleTemplateClick: MenuProps['onClick'] = ({ key }) => {
    const tpl = templates.find((t) => String(t.id) === String(key));
    if (!tpl) return;

    const prefill = getTemplatePrefill(tpl);

    draftForm.setFieldsValue({
      behavior_instructions: prefill.behavior_instructions,
      collect: prefill.collect,
    });

    snapshotRef.current = {
      instructions: String(prefill.behavior_instructions || ''),
      collect: [...prefill.collect],
    };

    setEditedAfterSnapshot(false);
    onSelectTemplate?.(tpl);
  };

  const canGenerate = canAction && editedAfterSnapshot;
  const canSave = advancedMode || (canAction && !editedAfterSnapshot);

  const handleToggleAdvancedMode = useCallback(() => {
    setAdvancedMode((prev) => {
      const next = !prev;

      if (next) {
        genReqIdRef.current += 1;
        setIsGenerating(false);
        setShowPreview(false);
        setGenResp(null);
        setPreviewTab('call');

        if (
          String(draftForm.getFieldValue('behavior_instructions') || '').trim()
        ) {
          draftForm.setFieldsValue({ behavior_instructions: '' });
        }
      }

      return next;
    });
  }, [draftForm, genReqIdRef]);

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCloseModal}
        footer={null}
        closable={false}
        centered
        width={1040}
        className='agentInstrModal'
        destroyOnClose
      >
        <Form
          form={draftForm}
          layout='vertical'
          className='agentInstrModal__form'
        >
          <div className='agentInstrModal__header'>
            <CustomTitle
              text='Set agent instructions'
              color='var(--text-primary)'
              fontSize='20px'
              lineHeight='28px'
              fontWeight='700'
              marginBottom='0px'
            />

            <div className='agentInstrModal__headerActions'>
              <Dropdown
                menu={{ items: templateItems, onClick: handleTemplateClick }}
                trigger={['click']}
                placement='bottomRight'
                disabled={templatesLoading || isGenerating}
              >
                <div className='agentInstrModal__templateTrigger'>
                  <TemplateIcon size={38} />
                </div>
              </Dropdown>

              <CloseOutlined
                className='agentInstrModal__close'
                onClick={handleCloseModal}
              />
            </div>
          </div>

          <div className='agentInstrModal__divider' />

          <div
            className={`agentInstrModal__body ${showPreview ? 'is-preview' : ''}`}
          >
            <div className='agentInstrModal__left'>
              <BehaviorSection
                dynVarOptions={dynVarOptions}
                onOpenDyn={onOpenDyn}
                isGenerating={isGenerating}
                onCancelGenerate={handleCancelGenerate}
                onUndo={handleUndoGenerate}
                canUndo={canUndoGenerate}
                onClearAll={handleClearAll}
                canClearAll={canClearAll}
                advancedMode={advancedMode}
                onToggleAdvancedMode={handleToggleAdvancedMode}
              />

              <div className='agentInstrModal__collectBlock'>
                <div className='agentInstrModal__label'>
                  Information to be collected
                </div>

                <Form.Item
                  name='collect'
                  className='agentInstrModal__collectItem'
                >
                  <CheckboxMultiSelect
                    disabled={isGenerating}
                    open={collectOpen}
                    setOpen={setCollectOpen}
                    showTagCloseIcon={true}
                    background='#5E5E5E'
                    color='#fff'
                    titleText='Information to be collected'
                    options={collectOptions}
                    placeholder={loadingDropdowns ? 'Loading...' : 'Select...'}
                    className='agentSelect agentSelect--darkText'
                    groupCustomVariables
                    onDeleteCustomOption={
                      onDeleteCustomVar
                        ? (value) => onDeleteCustomVar(String(value))
                        : undefined
                    }
                    onCreateCustomOption={onCreateCustomVar}
                    onUpdateCustomOption={onUpdateCustomVar}
                  />
                </Form.Item>
              </div>

              <div className='agentInstrModal__generateRow'>
                <CustomButton
                  text='Generate instructions'
                  onClick={async () => {
                    const beforeInstr = String(
                      draftForm.getFieldValue('behavior_instructions') || ''
                    );
                    const beforeCollectRaw =
                      (draftForm.getFieldValue('collect') as
                        (string | number)[] | undefined) || [];
                    const beforeCollect = beforeCollectRaw.map(String);

                    preGenRef.current = {
                      instructions: beforeInstr,
                      collect: beforeCollect,
                    };

                    preGenerateUiRef.current = {
                      showPreview,
                      previewTab,
                      genResp,
                    };

                    setIsGenerating(true);
                    setGenResp(null);
                    setShowPreview(false);

                    const reqId = ++genReqIdRef.current;

                    try {
                      const resp = await onGenerateInstructions?.(reqId, {
                        behavior_prompt: beforeInstr,
                        collect_ids: beforeCollect,
                      });

                      if (reqId !== genReqIdRef.current) return;
                      if (!isOpenRef.current) return;

                      if (resp) setGenResp(resp);

                      const afterInstr = resp
                        ? String(resp.improved_prompt || beforeInstr)
                        : beforeInstr;

                      let afterCollect = beforeCollect;

                      if (
                        resp &&
                        Array.isArray(resp.collect_variables) &&
                        resp.collect_variables.length
                      ) {
                        const mapped = resp.collect_variables
                          .map(
                            (name) =>
                              collectOptions.find((o) => o.label === name)
                                ?.value
                          )
                          .filter(
                            (v): v is string | number =>
                              v !== undefined && v !== null
                          )
                          .map((v) => String(v));

                        if (mapped.length) afterCollect = mapped;
                      }

                      if (
                        resp &&
                        Array.isArray(resp.new_custom_variables) &&
                        resp.new_custom_variables.length &&
                        onCreateCustomVar
                      ) {
                        const newIds: string[] = [];
                        for (const nv of resp.new_custom_variables) {
                          try {
                            const id = await onCreateCustomVar({
                              name: nv.name,
                              type: normalizeAiSuggestedVarType(nv.type),
                              description: nv.description || '',
                              sample_value: nv.sample_value || '',
                            });
                            if (id !== undefined && id !== null) {
                              newIds.push(String(id));
                            }
                          } catch {
                            // Best-effort: skip variables that fail to stage
                            // (e.g. name collides with an existing one).
                          }
                        }
                        if (newIds.length) {
                          afterCollect = [
                            ...new Set([...afterCollect, ...newIds]),
                          ];
                        }
                      }

                      draftForm.setFieldsValue({
                        behavior_instructions: afterInstr,
                        collect: afterCollect,
                      });

                      postGenRef.current = {
                        instructions: afterInstr,
                        collect: afterCollect,
                      };

                      snapshotRef.current = {
                        instructions: afterInstr,
                        collect: [...afterCollect],
                      };

                      setEditedAfterSnapshot(false);
                      setShowPreview(true);
                      setPreviewTab('call');
                    } finally {
                      if (reqId === genReqIdRef.current && isOpenRef.current) {
                        setIsGenerating(false);
                      }
                    }
                  }}
                  width='196px'
                  height='36px'
                  bgColor='var(--text-primary)'
                  color='var(--surface-container)'
                  borderRadius='8px'
                  fontSize='14px'
                  fontWeight='600'
                  marginBottom='15px'
                  boxShadow='none'
                  iconPosition='left'
                  icon={
                    <StarTickIcon
                      color={
                        canGenerate && !isGenerating && !advancedMode
                          ? 'var(--surface-container)'
                          : '#A6A6A6'
                      }
                    />
                  }
                  isFormValid={!canGenerate || isGenerating || advancedMode}
                  gap='8px'
                />
              </div>
            </div>

            <div
              className={`agentInstrModal__right ${
                bestPracticesCollapsed && !showPreview ? 'is-collapsed' : ''
              }`}
            >
              {!showPreview ? (
                // The card stays rendered when collapsed (hidden and clipped
                // to the 48px strip) so the strip is exactly as tall as it.
                <div
                  className={`agentInstrModal__bestWrap ${
                    bestPracticesCollapsed ? 'is-collapsed' : ''
                  }`}
                >
                  <BestPracticesPanel
                    onCollapse={() => setBestPracticesCollapsed(true)}
                  />
                  {bestPracticesCollapsed && (
                    <button
                      type='button'
                      className='agentInstrModal__collapseBtn agentInstrModal__expandBtn'
                      aria-label='Expand best practices'
                      onClick={() => setBestPracticesCollapsed(false)}
                    >
                      <VerticalRightOutlined />
                    </button>
                  )}
                </div>
              ) : (
                <div className='agentInstrModal__preview'>
                  <Tabs
                    activeKey={previewTab}
                    onChange={(k) => setPreviewTab(k as 'call' | 'email')}
                    items={[
                      {
                        key: 'call',
                        label: 'Call preview',
                        children: (
                          <div className='agentInstrModal__previewBody'>
                            <CallPreview
                              transcript={parseCallTranscript(
                                genResp?.call_transcript?.transcript
                              )}
                              agentName='AI assistant'
                              contactName='<contact name>'
                            />
                          </div>
                        ),
                      },
                      {
                        key: 'email',
                        label: 'Email preview',
                        children: (
                          <div className='agentInstrModal__previewBody'>
                            <EmailPreview
                              transcript={
                                genResp?.generated_email
                                  ? [
                                      {
                                        sender: companyName || 'Company',
                                        from: 'customer@zoomlogi.com',
                                        to: '<contact name>@<company name>.com',
                                        timestamp: new Date().toISOString(),
                                        body: (() => {
                                          const greeting =
                                            'Hi &lt;contact name&gt;,<br><br>';
                                          const body =
                                            genResp.generated_email
                                              .email_body || '';
                                          const signature = emailSignature
                                            ? `<br><br>${emailSignature.replace(/\n/g, '<br>')}`
                                            : '';
                                          return greeting + body + signature;
                                        })(),
                                      },
                                    ]
                                  : []
                              }
                            />
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </Form>
        <div className='agentInstrModal__footer'>
          <CustomButton
            text='Cancel'
            bgColor='var(--surface-container)'
            color='var(--text-primary)'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='0 16px'
            borderRadius='8px'
            onClick={handleCloseModal}
          />
          <CustomButton
            text='Save'
            bgColor='var(--text-primary)'
            isFormValid={!canSave}
            color='var(--surface-container)'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='0 16px'
            borderRadius='8px'
            onClick={() => {
              const instr = String(
                draftForm.getFieldValue('behavior_instructions') || ''
              );
              const collectRaw =
                (draftForm.getFieldValue('collect') as
                  (string | number)[] | undefined) || [];
              const collect = collectRaw.map(String);

              if (!collect.length) {
                setMissingCollectOpen(true);
                return;
              }

              form.setFieldsValue({
                behavior_instructions: instr,
                advanced_instruction_mode: advancedMode,
                collect,
              });

              snapshotRef.current = {
                instructions: instr,
                collect: [...collect],
              };

              setEditedAfterSnapshot(false);
              onSave?.();
            }}
          />
        </div>
      </Modal>

      <CustomModalWithoutButtons
        modalOpen={missingCollectOpen}
        setModalOpen={setMissingCollectOpen}
        modalTitle='Select at least one field to collect'
        modalSize={600}
        fontSize={'24px'}
        className='agentMissingCollectModal'
        zIndex={3100}
      >
        <p className='agentMissingCollectModal__text'>
          To save this agent, select at least one field the agent should collect
          under <strong>Information to be collected</strong>.
        </p>
        <div className='agentMissingCollectModal__footer'>
          <CustomButton
            text='Cancel'
            bgColor='transparent'
            color='var(--text-primary)'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='0 16px'
            borderRadius='8px'
            onClick={() => setMissingCollectOpen(false)}
          />
          <CustomButton
            text='Select fields'
            bgColor='var(--text-primary)'
            color='var(--surface-container)'
            boxShadow='none'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            padding='0 16px'
            borderRadius='8px'
            onClick={() => {
              setMissingCollectOpen(false);
              window.setTimeout(() => setCollectOpen(true), 0);
            }}
          />
        </div>
      </CustomModalWithoutButtons>
    </>
  );
};

export default AgentInstructionsModal;
